import { Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import { useCalculatorAccessQuery } from "web_myplann/Api/Calculators/useCalculatorAccessQuery";
import { CalculatorUnavailableDrawer } from "web_myplann/Calculators/Components/CalculatorUnavailableDrawer";
import { useCalculatorUnavailableDrawer } from "web_myplann/Calculators/Hooks/useCalculatorUnavailableDrawer";
import { useOpenCalculator } from "web_myplann/Calculators/Hooks/useOpenCalculator";
import { QuickCalculatorTile } from "web_myplann/Dashboard/Components/QuickCalculatorTile";
import { QuickCalculatorTileList } from "web_myplann/Dashboard/Components/QuickCalculatorTileList";
import { QUICK_CALCULATOR_TYPES } from "web_myplann/Dashboard/Components/quickCalculators";
import type { QuickCalculatorItem } from "web_myplann/Dashboard/Types/QuickCalculator";
import { mapQuickCalculatorItems } from "web_myplann/Dashboard/Utils/mapQuickCalculatorItems";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { HorizontalScrollContainer } from "web_myplann/Shared/Components/HorizontalScrollContainer";
import { useHorizontalDragScroll } from "web_myplann/Shared/Hooks/useHorizontalDragScroll";

const PageResources = Resources.Dashboard.QuickCalculators;

export const QuickCalculatorsSection: FC = () => {
	const { dashboard, palette, spacingTokens } = useTheme();
	const { tile } = dashboard.quickCalculator;
	const { t } = useResource();
	const { data: access, isLoading } = useCalculatorAccessQuery();
	const { scrollRef, onMouseDown, wasDragged } = useHorizontalDragScroll();
	const { openCalculator, openCalculatorList, isOpeningCalculator } =
		useOpenCalculator();
	const {
		unavailableType,
		isUnavailableOpen,
		openUnavailable,
		closeUnavailable,
	} = useCalculatorUnavailableDrawer();

	const calculators = mapQuickCalculatorItems(QUICK_CALCULATOR_TYPES, access);

	const isTapBlocked = () => wasDragged() || isOpeningCalculator;

	const handleCalculatorClick = ({ type, isLocked }: QuickCalculatorItem) => {
		if (isTapBlocked()) return;
		if (!access) {
			openCalculatorList();
			return;
		}
		if (isLocked) {
			openUnavailable(type);
			return;
		}
		openCalculator(type);
	};

	const handleAllCalculatorsClick = () => {
		if (isTapBlocked()) return;
		openCalculatorList();
	};

	return (
		<Stack>
			<Typography variant="sectionLabel">{t(PageResources.title)}</Typography>
			<HorizontalScrollContainer
				scrollRef={scrollRef}
				onMouseDown={onMouseDown}
				pb={spacingTokens.lg}
				pl={spacingTokens.lg}
				gap={spacingTokens.lg}
			>
				<QuickCalculatorTileList
					items={calculators}
					isLoading={isLoading}
					onSelect={handleCalculatorClick}
				/>
				<QuickCalculatorTile
					label={t(PageResources.allCalculators)}
					iconBg={palette.grey[200]}
					icon={
						<ChevronRight
							aria-hidden
							size={tile.iconSize}
							color={palette.grey[500]}
						/>
					}
					onClick={handleAllCalculatorsClick}
				/>
			</HorizontalScrollContainer>
			<CalculatorUnavailableDrawer
				open={isUnavailableOpen}
				type={unavailableType}
				onClose={closeUnavailable}
			/>
		</Stack>
	);
};
